import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import DocPageHeader from 'components/docs/DocPageHeader';
import { useAuth } from 'hooks/useAuth';
import { BillingInfo } from 'models/Billing';
import { SERVICE_AGREEMENT_PATH } from 'models/Agreement';
import { acceptAgreement, getBilling } from 'services/billingService';
import BillingStatusPanel from 'shiplab-components/BillingStatusPanel';
import StripePricingTable from 'shiplab-components/StripePricingTable';
import { isProviderBilling, isTrialOrActive } from 'utils/billing';

const pricingTableUnavailableMessage = (billing: BillingInfo): string => {
  if (!billing.checkout_eligible) {
    return 'Your agreement is saved, but checkout is not available for this account.';
  }
  if (!billing.billing_reference) {
    return 'Your agreement is saved, but this account is missing a billing reference.';
  }
  if (!billing.stripe?.publishable_key || !billing.stripe?.pricing_table_id) {
    return 'Your agreement is saved, but Stripe checkout is not configured on the server.';
  }
  return 'Your agreement is saved, but the pricing table is not available yet. Please refresh this page.';
};

const ProviderBillingPanel: React.FC = () => (
  <Card className="border border-200">
    <Card.Body>
      <h4 className="mb-2">Provider Plan</h4>
      <p className="mb-0 text-700">
        Your account is billed under a custom Shiplab agreement. Contact Shiplab
        for billing or plan changes.
      </p>
    </Card.Body>
  </Card>
);

const PlansBillingPage: React.FC = () => {
  const { accessToken, username } = useAuth();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const loadBilling = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getBilling(accessToken);
      setBilling(data);
      if (data.agreement_accepted) {
        setAgreementChecked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void loadBilling();
  }, [loadBilling]);

  const handleAcceptAgreement = async () => {
    if (!accessToken || !agreementChecked) {
      return;
    }
    setAccepting(true);
    setAcceptError(null);
    try {
      const result = await acceptAgreement(accessToken);
      if (result && 'show_pricing_table' in result) {
        setBilling(result);
      } else {
        await loadBilling();
      }
    } catch (err) {
      setAcceptError(
        err instanceof Error ? err.message : 'Failed to save agreement'
      );
    } finally {
      setAccepting(false);
    }
  };

  const showProvider = billing ? isProviderBilling(billing) : false;
  const showActiveStatus = billing ? isTrialOrActive(billing) : false;
  const showPricingTable =
    !!billing &&
    !!billing.show_pricing_table &&
    !!billing.agreement_accepted &&
    !!billing.stripe?.pricing_table_id &&
    !!billing.stripe?.publishable_key &&
    !!billing.billing_reference;
  const showCheckoutFlow =
    !!billing &&
    !showProvider &&
    !showActiveStatus &&
    (billing.checkout_eligible ||
      billing.billing_status === 'unconverted' ||
      billing.billing_status === 'canceled' ||
      billing.billing_status === 'past_due');

  return (
    <div>
      <DocPageHeader title="Plans & Billing" />

      {loading && (
        <div className="d-flex align-items-center gap-2 text-700 py-4">
          <Spinner animation="border" size="sm" />
          <span>Loading billing…</span>
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
          <div className="mt-2">
            <Button size="sm" variant="outline-danger" onClick={() => void loadBilling()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {!loading && !error && billing && (
        <>
          {showProvider && <ProviderBillingPanel />}

          {showActiveStatus && <BillingStatusPanel billing={billing} />}

          {showCheckoutFlow && (
            <>
              <div className="mb-4">
                <h3 className="mb-2">Activate your Shiplab trial</h3>
                <p className="text-700 mb-0">
                  Select a plan to start your 30-day trial. No charge today;
                  card required. Cancel anytime before your trial ends.
                </p>
              </div>

              {!billing.agreement_accepted && (
                <Card className="border border-200 mb-4">
                  <Card.Body>
                    <Form.Check
                      type="checkbox"
                      id="billing-agreement"
                      checked={agreementChecked}
                      disabled={accepting}
                      onChange={e => setAgreementChecked(e.target.checked)}
                      label={
                        <span>
                          I agree to the{' '}
                          <a
                            href={
                              billing.service_agreement_url ||
                              SERVICE_AGREEMENT_PATH
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Shiplab Service Agreement
                          </a>{' '}
                          and{' '}
                          <a
                            href={billing.terms_of_service_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms of Service
                          </a>
                          , and I confirm that I am authorized to agree on
                          behalf of my organization.
                        </span>
                      }
                    />
                    {acceptError && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        {acceptError}
                      </Alert>
                    )}
                    <div className="mt-3">
                      <Button
                        variant="primary"
                        disabled={!agreementChecked || accepting}
                        onClick={() => void handleAcceptAgreement()}
                      >
                        {accepting ? 'Saving…' : 'Confirm & continue'}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {showPricingTable && billing.stripe && (
                <Card className="border border-200">
                  <Card.Body>
                    <StripePricingTable
                      pricingTableId={billing.stripe.pricing_table_id}
                      publishableKey={billing.stripe.publishable_key}
                      customerEmail={username}
                      clientReferenceId={billing.billing_reference}
                    />
                  </Card.Body>
                </Card>
              )}

              {billing.agreement_accepted && !showPricingTable && (
                <Alert variant="warning">
                  {pricingTableUnavailableMessage(billing)}
                </Alert>
              )}
            </>
          )}

          {!showProvider && !showActiveStatus && !showCheckoutFlow && (
            <BillingStatusPanel billing={billing} />
          )}
        </>
      )}
    </div>
  );
};

export default PlansBillingPage;
